import OmSpinner from "@/components/OmSpinner";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLang } from "@/context/LanguageContext";
import { fetchCalendar, fetchMonthPanchang, fetchDayPanchang, fetchMonthMasa } from "@/lib/api";
import { useLivePanchang } from "@/lib/usePanchang";
import JourneyStrip from "@/components/JourneyStrip";
import { PageHero } from "@/components/PageHero";
import ShareButton from "@/components/ShareButton";
import MoonPhase from "@/components/MoonPhase";
import CivilTithiNote from "@/components/CivilTithiNote";
import { HERO_ART } from "@/lib/heroes";
import { stripHtml } from "@/lib/html";
import { CalendarDays, ArrowRight, Sparkles, ChevronLeft, ChevronRight, LayoutGrid, List, X, Sunrise, ShieldCheck, ShieldAlert } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const MONTHS_HI = ["जनवरी", "फ़रवरी", "मार्च", "अप्रैल", "मई", "जून", "जुलाई", "अगस्त", "सितंबर", "अक्टूबर", "नवंबर", "दिसंबर"];
const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WD_HI = ["रवि", "सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि"];
const YEAR = 2026;

const TYPE_CHIP = {
  festival: "bg-[rgba(199,80,0,0.12)] text-saffron",
  vrata: "bg-[rgba(184,134,11,0.14)] text-[var(--gold)]",
  ekadashi: "bg-[rgba(184,134,11,0.14)] text-[var(--gold)]",
  jayanti: "bg-[rgba(199,80,0,0.12)] text-saffron",
  sankranti: "bg-[rgba(46,125,50,0.12)] text-[#2E7D32]",
  shraddha: "bg-[rgba(120,87,194,0.12)] text-[#7E57C2]",
};

function fmtDate(iso, lang) {
  const d = new Date(iso + "T00:00:00");
  return { day: d.getDate(), mon: (lang === "hi" ? MONTHS_HI : MONTHS)[d.getMonth()].slice(0, 3), wd: (lang === "hi" ? WD_HI : WD)[d.getDay()] };
}

export default function Calendar() {
  const { pick, lang } = useLang();
  const { data: panchang } = useLivePanchang();
  const [festivals, setFestivals] = useState([]);
  const [month, setMonth] = useState(new Date().getFullYear() === YEAR ? new Date().getMonth() : 0);
  const [view, setView] = useState("grid");
  const [mp, setMp] = useState(null);
  const [masaDays, setMasaDays] = useState([]);   // engine per-day Amanta/Purnimanta masa
  const [sel, setSel] = useState(null);
  const [dayP, setDayP] = useState(null);   // engine panchang for the selected day (muhurta + tithi timing)

  useEffect(() => { fetchCalendar().then((d) => setFestivals(d.festivals || [])).catch(() => {}); }, []);
  // Fetch the selected day's muhurta + tithi timing from the engine.
  useEffect(() => {
    if (!sel) { setDayP(null); return; }
    setDayP(null);
    const lat = typeof panchang?.lat === "number" ? panchang.lat : undefined;
    const lon = typeof panchang?.lon === "number" ? panchang.lon : undefined;
    const tz = typeof panchang?.tz === "number" ? panchang.tz : undefined;
    fetchDayPanchang(sel.d.date, lat, lon, tz).then(setDayP).catch(() => setDayP(false));
  }, [sel, panchang?.lat, panchang?.lon, panchang?.tz]);
  useEffect(() => {
    setMp(null);
    // Only pass lat/lon/tz when numeric; useLivePanchang's panchang.tz is a
    // tz_id string (e.g. "Asia/Kolkata") which 422s the int-typed backend param.
    const lat = typeof panchang?.lat === "number" ? panchang.lat : undefined;
    const lon = typeof panchang?.lon === "number" ? panchang.lon : undefined;
    const tz = typeof panchang?.tz === "number" ? panchang.tz : undefined;
    fetchMonthPanchang(YEAR, month + 1, lat, lon, tz).then(setMp).catch(() => setMp({ days: [] }));
  }, [month, panchang?.lat, panchang?.lon, panchang?.tz]);
  // Engine-sourced lunar month (masa) names per day — Amanta + Purnimanta (Rule 46).
  useEffect(() => {
    setMasaDays([]);
    fetchMonthMasa(YEAR, month + 1).then((d) => setMasaDays(d.days || [])).catch(() => setMasaDays([]));
  }, [month]);

  const todayIso = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const upcoming = festivals.filter((f) => f.date >= todayIso).slice(0, 4);

  // Collapse per-day masa into display segments (name + day range) for a system.
  const masaSegs = (field) => {
    const segs = [];
    for (const x of masaDays) {
      const nm = x[field];
      if (!nm) continue;
      const dnum = parseInt(x.date.slice(8, 10), 10);
      const last = segs[segs.length - 1];
      if (last && last.name === nm) last.to = dnum;
      else segs.push({ name: nm, from: dnum, to: dnum });
    }
    return segs;
  };
  const MASA_DEVA = { Chaitra: "चैत्र", Vaishakha: "वैशाख", Jyeshtha: "ज्येष्ठ", Ashadha: "आषाढ़", Shravana: "श्रावण", Bhadrapada: "भाद्रपद", Ashwina: "आश्विन", Kartika: "कार्तिक", Margashirsha: "मार्गशीर्ष", Pausha: "पौष", Magha: "माघ", Phalguna: "फाल्गुन" };
  const masaName = (n) => pick(MASA_DEVA[n] || n, n);
  const renderSeg = (segs) => segs.map((s, i) => (
    <span key={i}>{i > 0 ? " → " : ""}{masaName(s.name)}{segs.length > 1 ? <span className="text-ink-3"> ({s.from === s.to ? s.from : `${s.from}–${s.to}`})</span> : null}</span>
  ));
  const amantaSegs = masaSegs("amanta");
  const purnimaSegs = masaSegs("purnimanta");

  // festivals for the active month, grouped by date
  const monthFests = festivals.filter((f) => parseInt(f.date.slice(5, 7), 10) === month + 1);
  const festByDate = {};
  monthFests.forEach((f) => { (festByDate[f.date] ||= []).push(f); });

  const days = mp?.days || [];
  const firstWd = days.length ? days[0].weekday : 0; // Mon=0..Sun=6 from engine
  const leadBlanks = (firstWd + 1) % 7; // convert to Sun-first grid offset

  return (
    <>
      <Helmet><title>Hindu Festival Calendar 2026 · Dharmartha</title>
        <meta name="description" content="A traditional Hindu festival calendar for 2026 — monthly grid with tithi, moon phase, festivals and vratas." /></Helmet>

      <PageHero
        art={HERO_ART.cosmos}
        overline={pick("त्योहार एवं व्रत", "Festivals & Observances")}
        deva="पर्व-पंचांग"
        en={pick("हिन्दू त्योहार कैलेंडर २०२६", "Hindu Festival Calendar 2026")}
        tagline={pick("मासिक पंचांग — तिथि, चन्द्र-कला, त्योहार एवं व्रत सहित।", "A monthly panchang grid with tithi, moon phase, festivals and vratas.")}
      />

      <section className="mx-auto max-w-6xl px-5 py-14 md:px-8 md:py-16">
        {/* Upcoming */}
        {upcoming.length > 0 && (
          <div className="mb-12" data-testid="calendar-upcoming">
            <div className="mb-5 flex items-center gap-3"><Sparkles className="h-4 w-4 text-saffron" /><h2 className="overline-label text-ink-3">{pick("आगामी", "Coming up next")}</h2></div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {upcoming.map((f) => {
                const d = fmtDate(f.date, lang);
                return (
                  <Link to={f.link || "/festivals"} key={f.date + f.name} className="border border-saffron/30 bg-[rgba(199,80,0,0.04)] p-5 transition-colors hover:border-saffron">
                    <p className="font-serif-latin text-3xl text-ink">{d.day} <span className="text-lg text-ink-3">{d.mon}</span></p>
                    <p className="mt-2 font-deva text-lg text-ink deva-lh">{f.deva}</p>
                    <p className="text-sm text-ink-2">{f.name}</p>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Samvat header + month nav + view toggle */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4 border-b border-line pb-4" data-testid="calendar-samvat-header">
          <div className="flex items-center gap-3">
            <button data-testid="cal-prev" onClick={() => setMonth((m) => (m + 11) % 12)} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-2 hover:border-saffron hover:text-saffron"><ChevronLeft className="h-4 w-4" /></button>
            <div className="text-center">
              <h2 className="font-serif-latin text-2xl text-ink">
                {pick(MONTHS_HI[month], MONTHS[month])} {YEAR}
                {amantaSegs.length > 0 && <span className="text-ink-3"> · {renderSeg(amantaSegs)}</span>}
              </h2>
              {amantaSegs.length > 0 ? (
                <p className="text-sm text-ink-3" data-testid="calendar-masa">
                  {pick("अमान्त", "Amanta")}: {renderSeg(amantaSegs)} · {pick("पूर्णिमान्त", "Purnimanta")}: {renderSeg(purnimaSegs)}
                  {mp && <> · {pick("विक्रम संवत", "Vikram Samvat")} {mp.vikram_samvat} · {pick("शक", "Shaka")} {mp.shaka_samvat}</>}
                </p>
              ) : mp?.masa && (
                <p className="text-sm text-ink-3">{pick("हिन्दू मास", "Hindu month")}: {mp.masa.en} ({mp.masa.deva}) · {pick("विक्रम संवत", "Vikram Samvat")} {mp.vikram_samvat} · {pick("शक", "Shaka")} {mp.shaka_samvat}</p>
              )}
            </div>
            <button data-testid="cal-next" onClick={() => setMonth((m) => (m + 1) % 12)} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-2 hover:border-saffron hover:text-saffron"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="hidden items-center gap-1 rounded-full border border-line p-0.5 sm:flex">
            <button data-testid="cal-view-grid" onClick={() => setView("grid")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${view === "grid" ? "bg-[var(--saffron)] text-white" : "text-ink-2"}`}><LayoutGrid className="h-4 w-4" /> {pick("ग्रिड", "Grid")}</button>
            <button data-testid="cal-view-list" onClick={() => setView("list")} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm ${view === "list" ? "bg-[var(--saffron)] text-white" : "text-ink-2"}`}><List className="h-4 w-4" /> {pick("सूची", "List")}</button>
          </div>
        </div>

        {/* Legend */}
        <div className="mb-4 flex flex-wrap items-center gap-4 text-xs text-ink-3">
          <span className="inline-flex items-center gap-1.5"><MoonPhase phase={4} size={13} /> {pick("पूर्णिमा", "Purnima")}</span>
          <span className="inline-flex items-center gap-1.5"><MoonPhase phase={0} size={13} /> {pick("अमावस्या", "Amavasya")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[rgba(199,80,0,0.6)]" /> {pick("त्योहार", "Festival")}</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full bg-[rgba(184,134,11,0.7)]" /> {pick("व्रत", "Vrata")}</span>
        </div>

        {!mp ? <OmSpinner /> : (
        <>
        {/* Mobile: readable full-width agenda of the whole month */}
        <div className="space-y-1.5 sm:hidden" data-testid="calendar-agenda">
          {days.map((d) => {
            const fests = festByDate[d.date] || [];
            const isToday = d.date === todayIso;
            return (
              <button key={d.date} data-testid={`agenda-day-${d.date}`} onClick={() => fests.length && setSel({ d, fests })}
                className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left ${isToday ? "border-saffron bg-[rgba(199,80,0,0.06)]" : "border-line bg-surface"} ${fests.length ? "" : "opacity-80"}`}>
                <div className="flex w-10 shrink-0 flex-col items-center">
                  <span className={`font-serif-latin text-xl leading-none ${isToday ? "text-saffron" : "text-ink"}`}>{d.day}</span>
                  <span className="text-[10px] uppercase text-ink-3">{(lang === "hi" ? WD_HI : WD)[(d.weekday + 1) % 7]}</span>
                </div>
                <MoonPhase phase={d.phase} size={18} className="shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-ink-3">{pick(d.paksha.deva, d.paksha.en)} {pick(d.tithi.deva, d.tithi.en)}</p>
                  {fests.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {fests.map((f) => (
                        <span key={f.id || f.name} className={`rounded px-1.5 py-0.5 text-[11px] font-medium leading-tight ${TYPE_CHIP[f.type] || "bg-[var(--parchment)] text-ink-2"}`}>{pick(f.name_hi, f.name)}</span>
                      ))}
                    </div>
                  )}
                </div>
                {fests.length > 0 && <ChevronRight className="h-4 w-4 shrink-0 text-ink-3" />}
              </button>
            );
          })}
        </div>

        {/* Desktop: grid or festival list (respects the toggle) */}
        <div className="hidden sm:block">
        {view === "grid" ? (
          <div data-testid="calendar-grid" className="overflow-hidden rounded-xl border border-line">
            <div className="grid grid-cols-7 bg-[var(--ink)] text-parchment">
              {(lang === "hi" ? WD_HI : WD).map((w) => <div key={w} className="px-1 py-2 text-center text-xs font-medium uppercase tracking-wide">{w}</div>)}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: leadBlanks }).map((_, i) => <div key={`b${i}`} className="min-h-[84px] border-b border-r border-line bg-[var(--parchment)]/30 md:min-h-[110px]" />)}
              {days.map((d) => {
                const fests = festByDate[d.date] || [];
                const isToday = d.date === todayIso;
                const clickable = fests.length > 0;
                return (
                  <div key={d.date} data-testid={`cal-day-${d.date}`}
                    onClick={() => clickable && setSel({ d, fests })}
                    className={`relative min-h-[84px] border-b border-r border-line p-1.5 md:min-h-[110px] md:p-2 ${isToday ? "bg-[rgba(199,80,0,0.06)] ring-1 ring-inset ring-saffron/50" : "bg-surface"} ${clickable ? "cursor-pointer transition-colors hover:bg-[rgba(184,134,11,0.08)]" : ""}`}>
                    <div className="flex items-start justify-between">
                      <span className={`font-serif-latin text-base ${isToday ? "font-bold text-saffron" : "text-ink"}`}>{d.day}</span>
                      <MoonPhase phase={d.phase} size={14} />
                    </div>
                    <p className="mt-0.5 truncate text-[10px] text-ink-3" title={pick(d.tithi.deva, d.tithi.en)}>{pick(d.paksha.deva.slice(0, 1), d.paksha.en.slice(0, 1))}· {pick(d.tithi.deva, d.tithi.en)}</p>
                    <div className="mt-1 space-y-0.5">
                      {fests.slice(0, 2).map((f) => (
                        <p key={f.name} className={`truncate rounded px-1 py-0.5 text-[9px] font-medium leading-tight ${TYPE_CHIP[f.type] || "bg-[var(--parchment)] text-ink-2"}`} title={pick(f.name_hi, f.name)}>{pick(f.name_hi, f.name)}</p>
                      ))}
                      {fests.length > 2 && <p className="px-1 text-[9px] text-ink-3">+{fests.length - 2}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="calendar-list">
            {monthFests.length === 0 ? <p className="py-12 text-center text-ink-3">{pick("इस माह कोई प्रमुख त्योहार नहीं।", "No major festivals listed this month.")}</p> : monthFests.map((f) => {
              const d = fmtDate(f.date, lang);
              return (
                <div key={f.date + f.name} className="group flex items-start gap-4 border border-line bg-surface p-4 transition-colors hover:border-[var(--gold)] md:p-5" data-testid={`festival-${f.date}`}>
                  <div className="flex w-16 shrink-0 flex-col items-center border-r border-line pr-4 text-center">
                    <span className="font-serif-latin text-2xl text-ink">{d.day}</span>
                    <span className="text-xs text-ink-3">{d.wd}</span>
                  </div>
                  <Link to={f.link || "/festivals"} data-testid={`festival-link-${f.date}`} className="group/flink min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-deva text-lg text-ink deva-lh group-hover/flink:text-saffron">{f.deva}</h3>
                      <span className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${TYPE_CHIP[f.type] || "text-ink-3 bg-[var(--parchment)]"}`}>{f.type}</span>
                    </div>
                    <p className="text-sm font-medium text-ink-2">{pick(f.name_hi, f.name)}</p>
                    <p className="mt-1 text-sm leading-relaxed text-ink-3">{stripHtml(pick(f.significance_hi, f.significance))}</p>
                  </Link>
                  <ShareButton variant="icon" testid={`festival-share-${f.date}`} title={`${pick(f.name_hi, f.name)} · ${f.date}`} text={`${pick(f.name_hi, f.name)} (${f.deva})`} />
                </div>
              );
            })}
          </div>
        )}
        </div>
        </>
        )}

        <div className="mt-12 flex flex-wrap items-center gap-4 border-t border-line pt-8">
          <p className="text-ink-2">{pick("किसी पूजा या अनुष्ठान की योजना बना रहे हैं?", "Planning a pooja for a festival?")}</p>
          <Link to="/services/pooja" className="inline-flex items-center gap-2 border border-[var(--ink)] px-6 py-3 text-sm font-medium text-ink transition-colors hover:bg-ink hover:text-[var(--surface)]">
            {pick("पूजा बुक करें", "Book a pooja")} <ArrowRight className="h-4 w-4" />
          </Link>
          <ShareButton label={pick("कैलेंडर साझा करें", "Share this calendar")} testid="calendar-share" />
        </div>
        <CivilTithiNote className="mt-6" />
        <p className="mt-4 text-xs italic text-ink-3">{pick("तिथि एवं चन्द्र-कला खगोलीय गणना (लाहिरी) पर आधारित; क्षेत्रीय भिन्नता संभव है।", "Tithi and moon phase are computed astronomically (Lahiri); regional variation may occur.")}</p>
        <JourneyStrip links={["panchang", "rituals", "festivals", "mantras", "deities"]} />
      </section>

      {sel && (
        <div className="fixed inset-0 z-[90] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" onClick={() => setSel(null)} data-testid="cal-day-modal">
          <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-[var(--surface)] p-6 sm:rounded-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <p className="font-serif-latin text-xl text-ink">{sel.d.day} {pick(MONTHS_HI[month], MONTHS[month])} {YEAR}</p>
                <p className="mt-0.5 flex items-center gap-2 text-sm text-ink-3"><MoonPhase phase={sel.d.phase} size={14} /> {pick(sel.d.paksha.deva, sel.d.paksha.en)} {pick(sel.d.tithi.deva, sel.d.tithi.en)} · {pick(sel.d.nakshatra.deva, sel.d.nakshatra.en)}</p>
              </div>
              <button onClick={() => setSel(null)} className="flex h-8 w-8 items-center justify-center rounded-full text-ink-3 hover:bg-[var(--parchment)]"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              {sel.fests.map((f) => (
                <div key={f.id || f.name} className="border border-line bg-[var(--parchment)]/40 p-4" data-testid={`modal-fest-${f.id || ""}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-deva text-lg text-ink deva-lh">{f.deva || f.name}</h3>
                    <span className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${TYPE_CHIP[f.type] || "bg-[var(--parchment)] text-ink-2"}`}>{f.type}</span>
                  </div>
                  <p className="text-sm font-medium text-ink-2">{pick(f.name_hi, f.name)}</p>
                  {(f.significance || f.significance_hi) && <p className="mt-2 text-sm leading-relaxed text-ink-3">{stripHtml(pick(f.significance_hi, f.significance))}</p>}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Link to={f.link || "/festivals"} onClick={() => setSel(null)} data-testid={`modal-fest-link-${f.id || ""}`} className="inline-flex items-center gap-1.5 border border-saffron px-4 py-1.5 text-xs font-medium text-saffron transition-colors hover:bg-[var(--saffron)] hover:text-white">
                      {f.link ? pick("पूर्ण विवरण / पूजा विधि", "Full details & Puja Vidhi") : pick("त्योहार देखें", "View festival")} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                    <ShareButton variant="icon" testid={`modal-share-${f.id || ""}`} title={`${pick(f.name_hi, f.name)} · ${sel.d.day} ${pick(MONTHS_HI[month], MONTHS[month])} ${YEAR}`} text={`${pick(f.name_hi, f.name)} (${f.deva || ""})`} />
                  </div>
                </div>
              ))}
            </div>

            {/* Day timing + muhurta — from the Jyotish engine for this exact date */}
            {dayP === null && <div className="mt-4 h-20 animate-pulse rounded-lg border border-line bg-[var(--parchment)]/40" data-testid="modal-day-loading" />}
            {dayP && (
              <div className="mt-4 space-y-3" data-testid="modal-day-muhurta">
                <div className="rounded-lg border border-line bg-surface p-4">
                  <p className="overline-label mb-2 text-ink-3">{pick("तिथि एवं समय", "Tithi & timing")}</p>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-2">
                    <p className="text-ink-2"><span className="text-ink-3">{pick("तिथि", "Tithi")}:</span> {pick(dayP.tithi.deva, dayP.tithi.en)} <span className="text-ink-3">· {pick("समाप्ति", "ends")} {dayP.tithi.ends}</span></p>
                    <p className="text-ink-2"><span className="text-ink-3">{pick("नक्षत्र", "Nakshatra")}:</span> {pick(dayP.nakshatra.deva, dayP.nakshatra.en)} <span className="text-ink-3">· {pick("समाप्ति", "ends")} {dayP.nakshatra.ends}</span></p>
                    <p className="flex items-center gap-1.5 text-ink-2"><Sunrise className="h-3.5 w-3.5 text-saffron" /> {pick("सूर्योदय", "Sunrise")} {dayP.sunrise} · {pick("सूर्यास्त", "Sunset")} {dayP.sunset}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-lg border border-[#2E7D32]/30 bg-[rgba(46,125,50,0.06)] p-4" data-testid="modal-muhurta-good">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-[#2E7D32]"><ShieldCheck className="h-4 w-4" /> {pick("शुभ मुहूर्त", "Auspicious")}</p>
                    <ul className="space-y-1 text-sm text-ink-2">
                      <li className="flex justify-between gap-3"><span>{pick("अभिजित", "Abhijit")}</span><span className="text-ink-3">{dayP.muhurta.abhijit}</span></li>
                      <li className="flex justify-between gap-3"><span>{pick("ब्रह्म मुहूर्त", "Brahma Muhurta")}</span><span className="text-ink-3">{dayP.muhurta.brahma}</span></li>
                    </ul>
                  </div>
                  <div className="rounded-lg border border-saffron/30 bg-[rgba(199,80,0,0.05)] p-4" data-testid="modal-muhurta-avoid">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-saffron"><ShieldAlert className="h-4 w-4" /> {pick("त्याज्य काल", "Avoid")}</p>
                    <ul className="space-y-1 text-sm text-ink-2">
                      <li className="flex justify-between gap-3"><span>{pick("राहु काल", "Rahu Kalam")}</span><span className="text-ink-3">{dayP.muhurta.rahu_kalam}</span></li>
                      <li className="flex justify-between gap-3"><span>{pick("यमगण्ड", "Yamaganda")}</span><span className="text-ink-3">{dayP.muhurta.yamaganda}</span></li>
                      <li className="flex justify-between gap-3"><span>{pick("गुलिक काल", "Gulika Kalam")}</span><span className="text-ink-3">{dayP.muhurta.gulika_kalam}</span></li>
                    </ul>
                  </div>
                </div>
                <CivilTithiNote />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
